/**
 * Protected routes can render before the browser finishes exchanging the
 * server-owned session cookie into the temporary in-memory Firebase user that
 * legacy browser API hooks still depend on.
 *
 * During that gap, `getAuth(...).currentUser` is often `null`, which causes
 * request helpers to send an empty `x-user-token` header. Upstream services
 * then reject those refresh-time requests as 401/403 even when the durable
 * server session is valid.
 *
 * This module provides a tiny per-runtime barrier so browser API helpers can
 * wait for the active AuthGate bootstrap pass to settle before asking Firebase
 * for an ID token.
 */

type ClientAuthBootstrapState = {
  cycle: number;
  isPending: boolean;
  promise: Promise<void>;
  resolve: (() => void) | null;
};

function createResolvedState(cycle: number): ClientAuthBootstrapState {
  return {
    cycle,
    isPending: false,
    promise: Promise.resolve(),
    resolve: null,
  };
}

function createPendingState(cycle: number): ClientAuthBootstrapState {
  let resolve: (() => void) | null = null;
  const promise = new Promise<void>((fulfill) => {
    resolve = fulfill;
  });

  return {
    cycle,
    isPending: true,
    promise,
    resolve,
  };
}

let clientAuthBootstrapState = createResolvedState(0);

export function beginClientAuthBootstrap() {
  const nextCycle = clientAuthBootstrapState.cycle + 1;
  clientAuthBootstrapState = createPendingState(nextCycle);

  return nextCycle;
}

export function completeClientAuthBootstrap(cycle: number) {
  if (
    !clientAuthBootstrapState.isPending ||
    clientAuthBootstrapState.cycle !== cycle
  ) {
    return;
  }

  clientAuthBootstrapState.isPending = false;
  clientAuthBootstrapState.resolve?.();
  clientAuthBootstrapState.resolve = null;
}

export async function waitForClientAuthBootstrap(timeoutMs = 5_000) {
  if (!clientAuthBootstrapState.isPending) {
    return;
  }

  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  try {
    await Promise.race([
      clientAuthBootstrapState.promise,
      new Promise<void>((resolve) => {
        timeoutId = setTimeout(resolve, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

export function resetClientAuthBootstrapStateForTests() {
  clientAuthBootstrapState = createResolvedState(0);
}
